import React from "react";
import { ReactNode, FC, useEffect, useState } from "react";

const isFunction = (func: any): func is Function => typeof func === "function";
const isEqual = (a: any, b: any) => a === b;

type ClauseTuple = [any, ReactNode];

const makeClause =
  (compare: (a: any, b: any) => boolean) =>
  ([condition, result]: ClauseTuple): ClauseTuple =>
    isFunction(condition) ? [condition, result] : [(x: any) => compare(x, condition), result];

const findFirst = (arr: any[], cond: (val: any, index: number) => boolean) => arr.find((val, i) => cond(val, i));

interface PatternMatchProps {
  value: any;
  children?: ClauseTuple[];
  compare?: (a: any, b: any) => boolean;
}
const PatternMatch: FC<PatternMatchProps> = ({ children, value, compare = isEqual }) => {
  const [_forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    setForceUpdate((prev) => prev + 1);
  }, [compare, value]);

  let clauses: ClauseTuple[] | ReactNode[] = [];

  if (Array.isArray(children)) {
    clauses = children as ClauseTuple[];
  } else {
    clauses = [children] as ReactNode[];
  }

  const normalized = clauses.map((clause, _idx) =>
    Array.isArray(clause) ? makeClause(compare)(clause as ClauseTuple) : [_, clause],
  );

  const [_condition, result] = findFirst(normalized as ClauseTuple[], ([condition]) => condition(value));

  return <>{React.cloneElement(result as React.ReactElement, { key: _forceUpdate + "-" + result.toString() })}</>;
};

const _ = () => true;
const value = (name: string, condition: (val: any) => boolean) => (val: any) => condition(val[name]);

const wrap =
  (fn: Function, count = 1) =>
  (...args: any[]) => {
    if (args.length >= count + 1) {
      const [name, ...params] = args;
      return value(name, fn(...params));
    } else {
      return fn(...args);
    }
  };

const leq = wrap((x: any) => (y: any) => x == y);
const eq = wrap((x: any) => (y: any) => x === y);
const gt = wrap((x: any) => (y: any) => y > x);
const lt = wrap((x: any) => (y: any) => y < x);
const gte = wrap((x: any) => (y: any) => y >= x);
const lte = wrap((x: any) => (y: any) => y <= x);
const between = wrap((x: any, y: any) => (val: any) => x < val && val < y, 2);
const betweenInclusive = wrap((x: any, y: any) => (val: any) => x <= val && val <= y, 2);
const isArray = wrap((x: any) => Array.isArray(x));
const isString = wrap((x: any) => typeof x === "string");
const isNumber = wrap((x: any) => typeof x === "number");
const isObject = wrap((x: any) => x !== null && typeof x === "object");
const isEmpty = wrap((x: any) => {
  if (isArray(x) || isString(x)) {
    return x.length === 0;
  }
  if (isObject(x)) {
    return Object.keys(x).length === 0;
  }
  return false;
});
const hasKey = wrap((key: string) => (obj: any) => obj && obj.hasOwnProperty(key));
const none =
  (...validators: Function[]) =>
  (val: any) =>
    validators.every((validator) => !validator(val));
const any =
  (...validators: Function[]) =>
  (val: any) =>
    validators.some((validator) => validator(val));
const ifThen = (condition: Function, consequence: Function) => (val: any) => condition(val) ? consequence(val) : val;
const ifThenElse = (condition: Function, consequence: Function, alternative: Function) => (val: any) =>
  condition(val) ? consequence(val) : alternative(val);
const hasLengthOf = wrap((length: number) => (x: any) => x.length === length);
const matchesRegex = wrap((regex: RegExp) => (x: string) => regex.test(x));
const isOfType = wrap((type: string) => (x: any) => typeof x === type);
const isPositive = wrap((x: number) => x > 0);
const isNegative = wrap((x: number) => x < 0);
const isIn = wrap((arr: any[]) => (x: any) => arr.includes(x));
const implies = (p: Function, q: Function) => (val: any) => !p(val) || q(val);
const isEven = wrap((x: number) => x % 2 === 0);
const isOdd = wrap((x: number) => x % 2 !== 0);
const isInstanceOf = wrap((cls: any) => (x: any) => x instanceof cls);
const isFloat = wrap((n: any) => Number(n) === n && n % 1 !== 0);
const isInt = wrap((n: any) => Number(n) === n && n % 1 === 0);

const isTrue = eq(true);
const isFalse = eq(false);
const isNull = eq(null);
const isUndefined = eq(undefined);

const and = (x: Function, y: Function) => (val: any) => x(val) && y(val);
const or = (x: Function, y: Function) => (val: any) => x(val) || y(val);
const not = (x: Function) => (val: any) => !x(val);
const nand = (x: Function, y: Function) => (val: any) => !(x(val) && y(val));
const nor = (x: Function, y: Function) => (val: any) => !(x(val) || y(val));
const xnor = (x: Function, y: Function) => (val: any) => (x(val) && y(val)) || (!x(val) && !y(val));
const xor = (x: Function, y: Function) => (val: any) => (x(val) && !y(val)) || (!x(val) && y(val));

export {
  PatternMatch,
  isTrue,
  isFalse,
  isNull,
  isUndefined,
  _,
  eq,
  leq,
  gt,
  lt,
  gte,
  lte,
  between,
  betweenInclusive,
  and,
  or,
  not,
  nand,
  nor,
  xnor,
  xor,
  value,
  isArray,
  isString,
  isNumber,
  isObject,
  isEmpty,
  hasKey,
  none,
  any,
  ifThen,
  ifThenElse,
  hasLengthOf,
  matchesRegex,
  isOfType,
  isPositive,
  isNegative,
  isIn,
  implies,
  isEven,
  isOdd,
  isInstanceOf,
  isFloat,
  isInt,
};
